import { Observable, catchError, defer, map, of, retry, tap, throwError, timeout, timer, TimeoutError } from 'rxjs';
import { isArray } from 'lodash';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { LoggerService } from '../../services/logger.service';
import { ConsumerPortalApiService } from '../../services/consumer-portal-api.service';
import { PaymentData, PaymentMetaData, PaymentMinMaxBoundary, PaymentResponse } from '../models/payment-data.model';
import { Processors } from '../models/processors.interface';
import { ConsumerPortalConfig, CPEnvironment } from '../../models';
import { ConsumerPortalEnviromentConfig } from '../../models/environment-consumer-portal';
import { ContractCP, Payment } from '../../models/contract-model';
import { Invoice } from '../../models/invoice-model';
import { PaymentAppPayInput, PaymentCustomerDataInput } from '../models/paymentInput';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../store/app-wide/app/app.state';
import { getCustomerPaymentData } from '../../store/domains/customer/customer.selectors';
import { Customer } from '../../models/consumer';
import { PaymentAppPayOutput } from '../models/paymentOutput';
import { PaymentMethodEnums } from '../../enums/payment-method.enum';
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_CURRENCY, DEFAULT_PAYMENT_MID } from '../../constants/default.const';

// Signals "the boundary minimum hasn't changed yet" to retry({ delay }) below — not a real failure.
class BoundaryUnchangedError extends Error {
    constructor(readonly boundaries: PaymentMinMaxBoundary[]) {
        super('Payment boundary minimum has not changed yet');
    }
}

@Injectable({
    providedIn: 'root'
})
export class PorPayService {
    private readonly store = inject<Store<AppState>>(Store);
    private readonly logger = inject(LoggerService);
    private readonly consumerPortalApi = inject(ConsumerPortalApiService);
    private readonly translateService = inject(TranslateService);
    private readonly appConfig = inject<ConsumerPortalEnviromentConfig>(CPEnvironment);

    loadScriptForPOR() {
        this.logger.logWarning('Will Load Script Here');
    }

    createPayment(
        /* eslint-disable @typescript-eslint/naming-convention */
        /**
         * Note camelCase : DB Model
         */
        CustomerGuid: string,
        CurrencyCode: string,
        Amount_Requested: number,
        OrganizationId: string,
        RemoteDepotId: string,
        contractIds: string[],
        amountBeingPaidBeforeFee: number,
        metadata: PaymentMetaData,
        invoiceIds?: string[]
    ): Observable<PaymentResponse> {
        return this.consumerPortalApi
            .post<PaymentResponse>({
                controller: `payment/${CustomerGuid}/create-transaction`,
                method: 'POST',
                body: {
                    CustomerGuid: String(CustomerGuid),
                    Amount_Requested: Number(Amount_Requested),
                    CurrencyCode: String(CurrencyCode),
                    RemoteDepotId: String(RemoteDepotId),
                    contractIds,
                    invoiceIds,
                    amountBeingPaidBeforeFee,
                    metadata
                }
            })
            .pipe(
                tap({
                    error: (err: HttpErrorResponse) => {
                        this.logger.logWarning(err?.message);
                        this.logger.alertDevError(err);
                        return err;
                    },
                    next: (response: PaymentResponse) => {
                        this.logger.logInfo(response);
                        return response?.data;
                    }
                })
            );
    }

    verifyPayment(customerId: string, paymentData: PaymentData): Observable<Payment> {
        return this.consumerPortalApi
            .post<Payment>({
                controller: `payment/${customerId}/verify-transaction`,
                method: 'POST',
                body: paymentData,
                camelCase: false
            })
            .pipe(
                tap({
                    error: (err: HttpErrorResponse) => {
                        return err;
                    },
                    next: (response: Payment) => {
                        return response;
                    }
                })
            );
    }

    getMinMaxFromPaymentBoundary(customerId: string, contractId: string): Observable<PaymentMinMaxBoundary[]> {
        return this.consumerPortalApi.get<PaymentMinMaxBoundary>(`payment/${customerId}/get-boundary/${contractId}`).pipe(
            timeout(PorPayService.BOUNDARY_FETCH_TIMEOUT_MS),
            tap({
                next: (data: PaymentMinMaxBoundary[]) => {
                    return data;
                },
                error: (error: HttpErrorResponse) => {
                    return error;
                }
            })
        );
    }

    // Bounded exponential backoff for "retry until the boundary changes": 5s, 10s, 20s, 40s (~75s total),
    // matching ADO-111669's backlog note. Composes around getMinMaxFromPaymentBoundary; does not alter it.
    private static readonly BOUNDARY_POLL_MAX_ATTEMPTS = 4;
    private static readonly BOUNDARY_POLL_BASE_DELAY_MS = 5000;
    // A request that never answers would otherwise hold the pending badge (or a serialized page-load check) forever;
    // retry() only reacts to errors.
    private static readonly BOUNDARY_FETCH_TIMEOUT_MS = 15000;

    private static minAmountOf(boundaries: PaymentMinMaxBoundary[] | null): number | null {
        return boundaries?.length ? Math.min(...boundaries.map(boundary => boundary.minAmount)) : null;
    }

    /**
     * Re-fetches the payment boundary until its minimum differs from `previousMinAmount`, or the retry
     * budget is exhausted — whichever comes first. Emits exactly once, with the settled boundaries
     * (changed, or the last fetch if retries were exhausted). Callers keep their own catchError/fail-safe
     * handling: a genuine fetch error here propagates to the caller unchanged, same as a single-shot
     * fetch would.
     */
    getMinMaxFromPaymentBoundaryUntilChanged(customerId: string, contractId: string, previousMinAmount: number | null): Observable<PaymentMinMaxBoundary[]> {
        // defer() re-invokes getMinMaxFromPaymentBoundary (and its underlying HTTP GET) fresh on every
        // retry subscription — without it, retry() would resubscribe to the single Observable instance
        // already produced by one getMinMaxFromPaymentBoundary() call, never issuing a new fetch.
        return defer(() => this.getMinMaxFromPaymentBoundary(customerId, contractId)).pipe(
            map(boundaries => {
                if (previousMinAmount !== null && PorPayService.minAmountOf(boundaries) === previousMinAmount) {
                    throw new BoundaryUnchangedError(boundaries);
                }
                return boundaries;
            }),
            retry({
                count: PorPayService.BOUNDARY_POLL_MAX_ATTEMPTS,
                delay: (error, retryCount) => {
                    if (error instanceof BoundaryUnchangedError || error instanceof TimeoutError) {
                        return timer(PorPayService.BOUNDARY_POLL_BASE_DELAY_MS * 2 ** (retryCount - 1));
                    }
                    return throwError(() => error); // a real fetch failure — don't retry it here, let it propagate
                }
            }),
            catchError(error => (error instanceof BoundaryUnchangedError ? of(error.boundaries) : throwError(() => error)))
        );
    }

    getPaymentBoundaryMultiContract(customerId: string, contractIds: string[], amount: number) {
        return this.consumerPortalApi
            .post<PaymentMinMaxBoundary[]>({
                controller: `payment/${customerId}/get-multi-boundary`,
                method: 'POST',
                body: {
                    contractIds,
                    amount
                }
            })
            .pipe(
                tap({
                    next: (data: PaymentMinMaxBoundary[]) => {
                        return data;
                    },
                    error: (error: HttpErrorResponse) => {
                        return error;
                    }
                })
            );
    }

    /**
     * Get the payment processors
     * @param {string} customerId
     * @returns Processors[]
     */
    private getProcessors(customerId?: string): Observable<Processors[]> {
        return this.consumerPortalApi.get<Processors>(`payment/${customerId ?? 'apikey'}/processors`).pipe(
            tap({
                next: (data: Processors[]) => {
                    return data;
                },
                error: (error: HttpErrorResponse) => {
                    return error;
                }
            })
        );
    }

    /**
     * Hide Make Payment button based on payment processors, no processors are setup, hide and if consumer facing
     * is not available then also hide
     * @param {string} customerId
     * @returns {Observable<boolean | Processors[]>} false or Processors[]
     */
    isPaymentProcessorAvailable(customerId?: string): Observable<boolean | Processors[]> {
        return this.getProcessors(customerId).pipe(
            map((processors: Processors[]) => {
                if (processors.length === 0) {
                    return false;
                }
                let availableProcessors: Processors[] = processors.filter(processor => processor.RemoteDepotId !== null);
                if (this.appConfig?.envName !== 'stage') {
                    availableProcessors = availableProcessors.filter(processor => processor?.IsCustomerFacing);
                }

                if (availableProcessors.length === 0) {
                    return false;
                }

                return availableProcessors;
            }),
            catchError((error: HttpErrorResponse) => {
                this.logger.logError(error, '', true);
                return of(false);
            })
        );
    }

    getPaymentConfiguration(AccessToken: string, transactionId: string): Observable<PaymentAppPayInput> {
        return this.getCustomerPaymentData().pipe(
            map(customerData => {
                const configuration: PaymentAppPayInput = {
                    /* eslint-disable @typescript-eslint/naming-convention */
                    /**
                     * Note camelCase : DB Model
                     */
                    AccessToken: AccessToken,
                    TransactionId: transactionId,
                    ApiUrl: this.appConfig.gapiApiBaseUrl,
                    CustomerData: customerData
                };

                return configuration;
            })
        );
    }

    getPaymentData(
        customer: Customer | undefined,
        organizationId: string,
        paymentOutput: PaymentAppPayOutput,
        contracts: ContractCP[],
        source: string,
        config: ConsumerPortalConfig,
        invoices: Invoice[] = []
    ): PaymentData {
        // prevent empty string for emails
        const supportEmail = config.supportEmail?.trim() === '' ? undefined : config.supportEmail;
        const customerEmail = paymentOutput?.Customer?.Email?.trim() === '' ? undefined : paymentOutput?.Customer?.Email;

        const phone: string = customer?.Phones?.[0]?.Number ? customer?.Phones?.[0]?.Number : '0000000000';
        const email: string = customerEmail ?? supportEmail ?? 'ecommalerts@pointofrental.com';

        let address = '';
        if (customer?.Addresses && customer.Addresses.length > 0) {
            address = customer.Addresses?.[0]?.Line1 ? customer.Addresses?.[0]?.Line1 : '';
        }

        return {
            organizationId: organizationId,
            amount: paymentOutput?.TransactionData?.Amount_Processed ? this.convertCurrency(paymentOutput?.TransactionData?.Amount_Processed) : 0,
            contractIds: contracts.map(c => c.contractId),
            invoiceIds: invoices.length ? invoices.map(i => i.Id) : undefined,
            referenceNumber: paymentOutput?.TransactionId ? paymentOutput?.TransactionId : '',
            currency: paymentOutput?.TransactionData?.CurrencyCode ? paymentOutput?.TransactionData?.CurrencyCode : DEFAULT_CURRENCY, // default to USD
            depotId: paymentOutput.TransactionData?.RemoteDepotId ?? DEFAULT_PAYMENT_MID,
            address,
            companyName: customer?.CompanyName ? customer?.CompanyName : 'POR',
            email,
            firstName: customer?.FirstName ? customer?.FirstName : customer?.Name,
            lastName: customer?.LastName ? customer?.LastName : customer?.Name,
            phone,
            source: source
        };
    }

    formatCurrencyForPorPay(input: string): string {
        const decimalIndex: number = input.indexOf('.');

        if (decimalIndex === -1) {
            return input + '00';
        }

        const wholeNumberPart: string = input.slice(0, decimalIndex);
        const fractionalPart: string = input.slice(decimalIndex + 1);
        const paddedFractionalPart: string = fractionalPart.padEnd(2, '0');

        return wholeNumberPart + paddedFractionalPart.slice(0, 2);
    }

    /**
     * Convert smallest denomination of number to actual value, eg. 100(cents) to $1
     * @param {number} amount
     * @returns {number}
     */
    convertCurrency(amount: number): number {
        const dollars = amount / 100;

        // Format the result to two decimal places
        return parseFloat(dollars.toFixed(2));
    }

    getCustomerPaymentData(): Observable<PaymentCustomerDataInput> {
        return this.store.pipe(select(getCustomerPaymentData));
    }

    getPaymentMethod(paymentMethod: PaymentMethodEnums | undefined): string {
        switch (paymentMethod) {
            case PaymentMethodEnums.CreditCard:
                return this.translateService.instant('PaymentMethod.CreditCard');
            case PaymentMethodEnums.DebitCard:
                return this.translateService.instant('PaymentMethod.DebitCard');
            case PaymentMethodEnums.GiftCard:
                return this.translateService.instant('PaymentMethod.GiftCard');
            case PaymentMethodEnums.ACH:
                return this.translateService.instant('PaymentMethod.ACH');
            case PaymentMethodEnums.Crypto:
                return this.translateService.instant('PaymentMethod.Crypto');
            default:
                return '';
        }
    }

    /**
     * Multiple contract payment
     *  1. MID is always DEFAULT_MID
     *  2. If processor not setup for DEFAULT_MID, than check for other MIDs
     *  3. Return empty if there are no MIDs available
     * Single contract payment
     *  1. if a payment is attempted at a store without a MID -  set MID to default MID
     *  2. MID is always DEFAULT_MID
     *  3. If CP Admin set defaultPaymentMid, then override
     *  4. If processor not setup for CP Admin defaultPaymentMid, than check for other MIDs
     *  5. Return empty if there are no MIDs available
     */
    getPaymentMid(firstContractRemoteDepotId: string | undefined, selectedContracts: string[], processors: Processors[] | boolean | null, orgDefaultPaymentMid?: string): string {
        const isMultiPayment = selectedContracts.length > 1;

        // No processors available
        if (!processors || !isArray(processors)) {
            return isMultiPayment ? DEFAULT_PAYMENT_MID : firstContractRemoteDepotId ?? DEFAULT_PAYMENT_MID;
        }

        const defaultMid = orgDefaultPaymentMid ? orgDefaultPaymentMid : DEFAULT_PAYMENT_MID;
        const isProcessorSetup = processors.some(processor => processor.RemoteDepotId === defaultMid && processor.IsCustomerFacing === 1);

        // If is single contract and depotId set and processor is setup for depotId, return depotId
        if (!isMultiPayment && firstContractRemoteDepotId && processors.some(processor => processor.RemoteDepotId === firstContractRemoteDepotId && processor.IsCustomerFacing === 1)) {
            return firstContractRemoteDepotId;
        }

        // If processor setup for default MID, return default MID
        if (isProcessorSetup) {
            return defaultMid;
        }

        // If processor not setup for default MID, check for other MIDs
        // Sort by string RemoteDepotId and find first available processor with IsCustomerFacing = 1
        const sortedProcessors = processors.sort((a, b) => {
            const aDepotId = a.RemoteDepotId?.toString().padStart(3, '0') ?? '';
            const bDepotId = b.RemoteDepotId?.toString().padStart(3, '0') ?? '';
            return aDepotId.localeCompare(bDepotId);
        });

        // find first available processor with IsCustomerFacing = 1
        const customerFacingProcessor = sortedProcessors.find(processor => processor.IsCustomerFacing === 1);
        if (customerFacingProcessor) {
            return customerFacingProcessor.RemoteDepotId ?? '';
        }

        return '';
    }
}
